import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  Renderer2,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { find, get, isFunction } from 'lodash';
import { ICellRendererParams } from 'ag-grid-community';

import {
  ISelectOptionsType,
  TrackByValue,
  XUX_GRID_ACTIONS,
  XuxGridAction,
} from '@shared/types';
import { AgRendererComponent } from 'ag-grid-angular';
import { trackByValue } from '@app/shared/utils';
import { BaseRendererComponent } from '../base-renderer.component';

@Component({
  selector: 'xvi-custom-select-renderer',
  templateUrl: 'custom-select-renderer.component.html',
  styleUrls: ['./custom-select-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class XVICustomSelectRendererComponent
  extends BaseRendererComponent
  implements AgRendererComponent, OnInit, OnDestroy
{
  public isDropdownOpen: boolean = false;
  public params: ICellRendererParams;

  public value: string;
  public options: ISelectOptionsType[] = [];
  public class: string = '';
  public disabled: boolean = false;
  public field: string;
  public emitItemAction: Function;

  public trackByValue: TrackByValue = trackByValue;

  public offClickListener: () => void;

  public constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    protected cd: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.offClickListener = this.renderer.listen(
      'document',
      'click',
      (event: MouseEvent) => {
        const targetElement = event.target as HTMLElement;
        if (!this.elementRef.nativeElement.contains(targetElement)) {
          this.isDropdownOpen = false;
          this.cd.markForCheck();
        }
      },
    );
  }

  public ngOnDestroy(): void {
    if (isFunction(this.offClickListener)) {
      this.offClickListener();
    }
    this.offClickListener = null;
  }

  public agInit(params: ICellRendererParams): void {
    this.params = params;
    this.options = get(params, 'options', []);
    this.field = get(params, 'column.colDef.field', '');

    this.class = get(params, 'class', '');
    this.disabled = get(this.params, 'disabled', null);
    this.emitItemAction = get(this.params, 'emitItemAction');
    this.value = get(this.params, 'value');
  }

  public refresh(): boolean {
    return false;
  }

  public finishEditing(): void {
    const action = this.createAction();

    if (isFunction(this.emitItemAction)) {
      this.emitItemAction(action);
    }
  }

  public createAction(): XuxGridAction {
    if (!isFunction(this.emitItemAction)) {
      return;
    }

    const type = XUX_GRID_ACTIONS.ITEM_DATA_CHANGED;
    const rowIndex: number = get(this.params, 'node.rowIndex', 0);
    const rowId: string = get(this.params, 'node.id', null);
    const row: object = get(this.params, 'node.data');
    const action: XuxGridAction = {
      type,
      payload: {
        rowIndex,
        rowId,
        field: this.field,
        value: this.value,
        rowData: [row],
      },
    };

    return action;
  }

  public selectOption(option: ISelectOptionsType): void {
    if (!this.disabled) {
      this.value = option.value as string;
      this.finishEditing();
      this.isDropdownOpen = false; // Close the dropdown after selecting an option
    }
  }

  public toggleDropdown(): void {
    if (!this.disabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  }

  public getSelectedOptionText(): string {
    const selectedOption = find(
      this.options,
      (option) => option.value === this.value,
    );

    return selectedOption ? selectedOption.text : 'Select an option';
  }
}
